import { useState } from "react";
import Button from "@/components/common/Button";
import { domainsToText, parseDomains } from "../format";

const FIELD =
  "w-full rounded-(--radius-md) border border-(--border-strong) bg-(--surface-1) px-3 py-2 text-[13px] text-(--text-primary) outline-none placeholder:text-(--text-muted) focus:border-(--brand-primary)";
const LABEL = "mb-1.5 block text-[13px] font-semibold text-(--text-primary)";

export default function PixelForm({ initialPixel, onSubmit, onCancel }) {
  const [label, setLabel] = useState(initialPixel?.label ?? "");
  const [domainsText, setDomainsText] = useState(
    domainsToText(initialPixel?.allowedDomains),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!label.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      await onSubmit({
        label: label.trim(),
        allowedDomains: parseDomains(domainsText),
      });
    } catch (err) {
      setError(err?.message || "Failed to save pixel");
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="flex flex-col gap-4">
        <div>
          <label htmlFor="pixel-label" className={LABEL}>
            Label <span className="text-(--status-danger)">*</span>
          </label>
          <input
            id="pixel-label"
            type="text"
            required
            autoFocus
            maxLength={100}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Main site"
            className={FIELD}
          />
          <p className="mt-1 text-[12px] text-(--text-muted)">
            Must be unique (case-insensitive).
          </p>
        </div>
        <div>
          <label htmlFor="pixel-domains" className={LABEL}>
            Allowed domains{" "}
            <span className="font-normal text-(--text-muted)">(optional)</span>
          </label>
          <textarea
            id="pixel-domains"
            rows={4}
            value={domainsText}
            onChange={(e) => setDomainsText(e.target.value)}
            placeholder={"example.com\nshop.example.org"}
            className={`${FIELD} font-mono`}
          />
          <p className="mt-1 text-[12px] text-(--text-muted)">
            One domain per line. A domain also matches its subdomains
            (example.com allows www.example.com). No wildcards, schemes or
            paths. This list only filters accidental or cross-site use; it is
            not authentication. Leave empty to accept any site, including email
            and no-referrer pages. Changes can take up to 30 seconds to apply.
          </p>
        </div>
        {error && (
          <p className="text-[13px] text-(--status-danger)" role="alert">
            {error}
          </p>
        )}
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={submitting || !label.trim()}>
          {submitting ? "Saving…" : "Save"}
        </Button>
      </div>
    </form>
  );
}
